import * as React from 'react';
import { connect } from 'react-redux';

import HelpDropDown from './components/help-dropdown/HelpDropdown';
import HelpHeaderButton from './components/help-header-button/HelpHeaderButton';
import HelpFAQSection from './components/help-faq-section/HelpFAQSection';
import HelpVideoList from './components/help-video-list/HelpVideoList';
import HelpVideoPopup from './components/help-video-popup/HelpVideoPopup';

import { fetchTutorialHistory, setActiveVideo, setOrganizationType } from './reducers/tutorial-videos';

import { distinctVideoTutorialList } from './util/videos';
import {
  ORGANIZATION_TYPE_BRAND_AGENCY,
  ORGANIZATION_TYPE_INFLUENCERS_AGENCY,
  isBrandsFocusedOrganization,
  isPlainBrandOrganization,
  isPlainInfluencerOrganization
} from 'utils/organization';

import type { VideoTutorialEntry } from './types/VideoTutorialEntry.type';
import type { TutorialHistoryEntry } from './types/HistoryResponse.type';
import type { Organization } from 'types';

type StateProps = {
  activeVideoId: string,
  pageVideoIds: Array<string>,
  userVideoIds: Array<string>,
  tutorialHistory: Array<TutorialHistoryEntry>,
  currentOrganization: Organization
};

type DispatchProps = {
  dispatchFetchTutorialHistory: () => void,
  dispatchSetOrganizationType: (b: string, a: boolean) => void,
  dispatchSetActiveVideo: (a?: string | null) => void
};

type Props = StateProps & DispatchProps;

function getUserType(currentOrganization: Organization) {
  const isAgency = Boolean(currentOrganization?.agencyOrganizationId);
  if (isAgency) {
    if (isPlainBrandOrganization(currentOrganization)) {
      return ORGANIZATION_TYPE_BRAND_AGENCY;
    }

    if (isPlainInfluencerOrganization(currentOrganization)) {
      return ORGANIZATION_TYPE_INFLUENCERS_AGENCY;
    }
  }

  if (!currentOrganization) {
    return '';
  }

  return currentOrganization.type;
}

function buildPlaylist(videos: Array<VideoTutorialEntry>, activeVideo?: string | null): Array<VideoTutorialEntry> {
  if (!activeVideo) {
    return [];
  }

  const activeVideoIndex = videos.findIndex(({ id }) => id === activeVideo);
  if (activeVideoIndex === -1) {
    return [];
  }

  return [
    ...videos.slice(activeVideoIndex),
    ...videos.slice(0, activeVideoIndex)
  ];
}

const HelpMenu = (
  {
    pageVideoIds,
    userVideoIds,
    tutorialHistory,
    activeVideoId,
    currentOrganization,
    dispatchSetActiveVideo,
    dispatchSetOrganizationType,
    dispatchFetchTutorialHistory
  }: Props
): JSX.Element => {
  React.useEffect(() => {
    if (currentOrganization) {
      dispatchSetOrganizationType(getUserType(currentOrganization), currentOrganization.subscriptionType === 'self-service');
      dispatchFetchTutorialHistory();
    }
  }, [currentOrganization?.id]);

  const [isExpanded, setExpanded] = React.useState(false);
  const hasActiveVideos = activeVideoId !== undefined;

  const pageVideos = distinctVideoTutorialList.filter(({ id }) => userVideoIds.includes(id) && pageVideoIds.includes(id));
  const restVideos = distinctVideoTutorialList.filter(({ id }) => userVideoIds.includes(id) && !pageVideoIds.includes(id));
  const orderedVideos = [...pageVideos, ...restVideos];
  const videosToBeWatched = distinctVideoTutorialList
    .filter(({ kind }) => {
      const historyEntry = tutorialHistory.find(video => video.type === kind);
      if (!historyEntry) {
        return true;
      }

      return historyEntry.shouldBeWatched;
    })
    .map(({ kind }) => kind);

  const playlist = buildPlaylist(orderedVideos, activeVideoId);

  const handlePlayVideo = (videoId: string) => {
    setExpanded(false);
    dispatchSetActiveVideo(videoId);
  };

  const faqUrl = isBrandsFocusedOrganization(currentOrganization)
    ? 'https://help.collabary.com/brand-faqs'
    : 'https://help.collabary.com/content-creator-faqs';

  return (
    <React.Fragment>
      <HelpHeaderButton
        hasVideos={pageVideos.length > 0}
        hasUnwatchedVideos={pageVideos.filter(video => videosToBeWatched.includes(video.kind)).length > 0}
        onClick={() => setExpanded(!isExpanded)}
      />
      <HelpDropDown
        isVisible={isExpanded}
        onClose={() => setExpanded(false)}
      >
        <HelpFAQSection url={faqUrl} />
        <HelpVideoList
          activeVideoId={activeVideoId}
          pageVideos={pageVideos}
          restVideos={restVideos}
          videosToBeWatched={videosToBeWatched}
          onVideoClick={handlePlayVideo}
        />
      </HelpDropDown>
      {hasActiveVideos && (
        <HelpVideoPopup
          isAutoplay
          playlist={playlist}
          onClose={() => dispatchSetActiveVideo(undefined)}
          onNextVideo={() => dispatchSetActiveVideo(playlist[1].id)}
          onPreviousVideo={() => dispatchSetActiveVideo(playlist[playlist.length - 1].id)}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({ tutorialVideos, organizations }): StateProps => ({
  activeVideoId: tutorialVideos.activeVideo,
  currentOrganization: organizations.currentOrganization,
  pageVideoIds: tutorialVideos.pageVideos,
  userVideoIds: tutorialVideos.userVideos,
  tutorialHistory: tutorialVideos.history
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchTutorialHistory: () => dispatch(fetchTutorialHistory()),
  dispatchSetOrganizationType: (type: string, isSelfService: boolean) => dispatch(setOrganizationType(type, isSelfService)),
  dispatchSetActiveVideo: (id?: string | null) => dispatch(setActiveVideo(id, true))
});

export default connect(mapStateToProps, mapDispatchToProps)(HelpMenu);
